import PropTypes from 'prop-types'
import React, { useState } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map } from 'immutable'
import _partial from 'lodash/partial'
import _reduce from 'lodash/reduce'
import _size from 'lodash/size'
import { connect } from 'react-redux'
import { truncate } from '../../theme/web-app/index'
import { fromNow } from '../../services/date-time/index'
import useCommunityEnabled from '../../services/community/shared/useCommunityEnabled'
import UserAvatar from '../UserAvatar/index'
import Link from '../Link/index'
import CommentEdit from '../CommentEdit/index'
import Button, { TYPES } from '../Button/index'
import CommentPlaceholder from '../CommentPlaceholder/index'

const MAX_COMMENT_LENGTH = 400

function shouldTruncate (comment) {
  return _size(comment.comment || '') > MAX_COMMENT_LENGTH
}

export function injectBetweenItems (items, insert) {
  return _reduce(
    items,
    (reduction, item, index) => {
      const insertIndex = (index * 2) + 1
      if (insertIndex < _size(items)) {
        reduction.splice(insertIndex, 0, insert(insertIndex))
      }
      return reduction
    },
    items,
  )
}

function renderTextNewLine (comment, insertIndex) {
  return <br key={`comment-br-${comment.id}-${insertIndex}`} />
}

function CommentText (props) {
  const { comment, commentStaticText } = props
  const [truncated, setTruncated] = useState(shouldTruncate(comment))

  const commentBody = comment.comment || ''
  const commentText = shouldTruncate(comment) && truncated
    ? truncate(commentBody, MAX_COMMENT_LENGTH)
    : commentBody
  const text = commentText
    ? injectBetweenItems(
      commentText.split(/\n/),
      _partial(renderTextNewLine, comment),
    )
    : ''
  return (
    <p className="comment__text">
      {text}
      {shouldTruncate(comment) ? (
        <Button
          type={TYPES.LINK}
          onClick={_partial(setTruncated, !truncated)}
          className="comment__truncate"
        >
          {
            truncated
              ? commentStaticText.getIn(['data', 'more'])
              : commentStaticText.getIn(['data', 'less'])
          }
        </Button>
      ) : null}
    </p>
  )
}

function getDisplayName (comment) {
  return comment.userProfile?.displayName || 'Friend of Gaia'
}

function getAvatarPath (comment) {
  return comment.userProfile?.avatar?.small || null
}

function Comment (props) {
  const {
    comment,
    loading,
    auth,
    editMode,
    createComment,
    updateComment,
    onSubmitComplete,
    commentStaticText,
  } = props

  const { enabled: communityEnabled } = useCommunityEnabled()

  if (loading) {
    return <CommentPlaceholder />
  }

  const displayName = getDisplayName(comment)
  const avatarPath = getAvatarPath(comment)
  const commentAuthorUuid = comment.userProfile?.uuid
  const profileLink = communityEnabled && commentAuthorUuid
    ? `/profile/${commentAuthorUuid}`
    : null
  const timestamp = comment.createdAt
    ? fromNow(new Date(comment.createdAt).getTime())
    : ''

  return (
    <article className="comment">
      <div className="comment__user">
        {profileLink ? (
          <Link to={profileLink} className="comment__profile-link">
            <UserAvatar
              path={avatarPath}
              name={displayName}
            />
          </Link>
        ) : (
          <UserAvatar
            path={avatarPath}
            name={displayName}
          />
        )}
        <header className="comment__header">
          {profileLink ? (
            <Link to={profileLink} className="comment__profile-link">
              <div className="comment__title">{displayName}</div>
            </Link>
          ) : (
            <div className="comment__title">{displayName}</div>
          )}
          <span className="comment__timestamp">
            {timestamp}
          </span>
        </header>
      </div>
      <div className="comment__body">
        {editMode ? (
          <CommentEdit
            className={['comment__edit-textarea']}
            comment={comment}
            auth={auth}
            showAvatar={false}
            createComment={createComment}
            updateComment={updateComment}
            onSubmitComplete={onSubmitComplete}
          />
        ) : (
          <CommentText comment={comment} commentStaticText={commentStaticText} />
        )}
      </div>
    </article>
  )
}

Comment.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  comment: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  createComment: PropTypes.func.isRequired,
  updateComment: PropTypes.func.isRequired,
  onSubmitComplete: PropTypes.func.isRequired,
}

export default connect((state) => ({
  commentStaticText: state.staticText.getIn(['data', 'comment'], Map()),
}))(Comment)
